// =======================
// src/componentes/perfil/SegurancaUsuario.js
// =======================

import React, { useContext, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";

import { useNavigate } from "react-router-dom";

import { api } from "@api/api";
import {
  tamanhosControlesGlobais,
  tamanhosModaisGlobais,
  tamanhosPaginasAdministrativas,
  textosGlobais,
} from "../../estilos/global";
import { CondicaoContext } from "../../contexto";

const obterMensagemErro = (resultado, mensagemPadrao) => {
  const mensagem = resultado?.message ?? resultado?.msg;

  if (Array.isArray(mensagem)) {
    return mensagem.join(" ");
  }

  return mensagem || mensagemPadrao;
};

const requisitosSenha = [
  {
    texto: "Mínimo de 8 caracteres",
    validar: (senha) => senha.length >= 8,
  },

  {
    texto: "Pelo menos uma letra maiúscula (A-Z)",
    validar: (senha) => /[A-Z]/.test(senha),
  },

  {
    texto: "Pelo menos uma letra minúscula (a-z)",
    validar: (senha) => /[a-z]/.test(senha),
  },

  {
    texto: "Pelo menos um número (0-9)",
    validar: (senha) => /\d/.test(senha),
  },

  {
    texto: "Pelo menos um caractere especial (@ $ ! % * ? &)",
    validar: (senha) => /[@$!%*?&]/.test(senha),
  },
];

export default function SegurancaUsuario() {
  const navigate = useNavigate();

  const { logout } = useContext(CondicaoContext);

  const [senhaAtual, setSenhaAtual] = useState("");

  const [novaSenha, setNovaSenha] = useState("");

  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState("");

  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);

  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);

  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);

  const [salvando, setSalvando] = useState(false);

  const [erro, setErro] = useState("");

  const [sucesso, setSucesso] = useState("");

  const requisitosAtendidos = useMemo(
    () => requisitosSenha.filter((item) => item.validar(novaSenha)).length,

    [novaSenha],
  );

  const senhaValida = requisitosAtendidos === requisitosSenha.length;

  const limparFormulario = () => {
    setSenhaAtual("");
    setNovaSenha("");
    setConfirmarNovaSenha("");

    setMostrarSenhaAtual(false);
    setMostrarNovaSenha(false);
    setMostrarConfirmacao(false);

    setErro("");
    setSucesso("");
  };

  const alterarSenha = async () => {
    setErro("");
    setSucesso("");

    if (!senhaAtual || !novaSenha || !confirmarNovaSenha) {
      setErro("Preencha todos os campos de senha.");

      return;
    }

    if (!senhaValida) {
      setErro("A nova senha ainda não atende a todos os requisitos.");

      return;
    }

    if (novaSenha !== confirmarNovaSenha) {
      setErro("A confirmação da nova senha não confere.");

      return;
    }

    setSalvando(true);

    try {
      const response = await fetch(api.updateProfilePassword(), {
        method: "PATCH",

        credentials: "include",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          senhaAtual,
          novaSenha,
          confirmarNovaSenha,
        }),
      });

      const resultado = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          obterMensagemErro(resultado, "Não foi possível alterar a senha."),
        );
      }

      setSucesso(
        resultado?.msg || "Senha alterada com sucesso. Faça login novamente.",
      );

      window.setTimeout(() => {
        logout();

        navigate("/login", {
          replace: true,
        });
      }, 1000);
    } catch (error) {
      setErro(
        error instanceof Error
          ? error.message
          : "Não foi possível alterar a senha.",
      );

      setSalvando(false);
    }
  };

  const campoSenha = ({
    label,
    value,
    onChange,
    mostrar,
    onToggle,
    placeholder,
  }) => (
    <TextField
      fullWidth
      required
      size="small"
      label={label}
      type={mostrar ? "text" : "password"}
      value={value}
      placeholder={placeholder}
      disabled={salvando}
      onChange={(event) => {
        onChange(event.target.value);

        setErro("");
        setSucesso("");
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              type="button"
              edge="end"
              size="small"
              aria-label={mostrar ? "Ocultar senha" : "Mostrar senha"}
              onClick={onToggle}
              disabled={salvando}
            >
              {mostrar ? (
                <VisibilityOffRoundedIcon fontSize="small" />
              ) : (
                <VisibilityRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",

        boxSizing: "border-box",

        border: "1px solid #d4e5f6",

        borderRadius: 1.5,

        bgcolor: "#ffffff",

        p: { xs: 1.25, md: 1 },
      }}
    >
      {/* =====================================================
          CABEÇALHO
      ===================================================== */}

      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 0.7,

          mb: 2,
        }}
      >
        <LockRoundedIcon
          sx={{
            color: "#087cf0",

            fontSize: 20,

            flexShrink: 0,
          }}
        />

        <Box>
          <Typography
            sx={{
              color: "#0767d8",

              fontSize: tamanhosPaginasAdministrativas.tituloSecao,

              fontWeight: 700,

              lineHeight: 1.15,
            }}
          >
            Segurança
          </Typography>

          <Typography
            sx={{
              mt: 0.05,

              color: "#6b7892",

              fontSize: tamanhosModaisGlobais.descricao,

              lineHeight: 1.2,
            }}
          >
            Altere sua senha de acesso ao sistema.
          </Typography>
        </Box>
      </Box>

      {erro && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            py: 0,
          }}
        >
          {erro}
        </Alert>
      )}

      {sucesso && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            py: 0,
          }}
        >
          {sucesso}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1fr) minmax(0, 1.1fr)",
          },

          gap: 1,

          alignItems: "start",
        }}
      >
        {/* CAMPOS */}

        <Box
          sx={{
            display: "grid",
            gap: 0.75,
          }}
        >
          {campoSenha({
            label: "Senha atual",

            value: senhaAtual,

            onChange: setSenhaAtual,

            mostrar: mostrarSenhaAtual,

            onToggle: () => setMostrarSenhaAtual((atual) => !atual),

            placeholder: "Digite sua senha atual",
          })}

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "1fr",
              },

              gap: 0.75,
            }}
          >
            {campoSenha({
              label: "Nova senha",

              value: novaSenha,

              onChange: setNovaSenha,

              mostrar: mostrarNovaSenha,

              onToggle: () => setMostrarNovaSenha((atual) => !atual),

              placeholder: "Digite a nova senha",
            })}

            {campoSenha({
              label: "Confirmar nova senha",

              value: confirmarNovaSenha,

              onChange: setConfirmarNovaSenha,

              mostrar: mostrarConfirmacao,

              onToggle: () => setMostrarConfirmacao((atual) => !atual),

              placeholder: "Confirme a nova senha",
            })}
          </Box>
        </Box>

        {/* REQUISITOS */}

        <Box
          sx={{
            width: "100%",

            boxSizing: "border-box",

            border: "1px solid #dcecff",

            borderRadius: 1.5,

            bgcolor: "#eef7ff",

            px: 1.15,

            py: { xs: 0.8, md: 0.5 },
          }}
        >
          <Typography
            sx={{
              color: "#0767d8",

              fontSize: tamanhosModaisGlobais.rotulo,

              fontWeight: 700,

              lineHeight: 1.2,

              mb: { xs: 0.5, md: 0.25 },
            }}
          >
            Requisitos da senha
          </Typography>

          <Box
            sx={{
              display: "grid",

              gap: { xs: 0.25, md: 0.125 },
            }}
          >
            {requisitosSenha.map((requisito) => {
              const atendido = requisito.validar(novaSenha);

              return (
                <Box
                  key={requisito.texto}
                  sx={{
                    display: "flex",

                    alignItems: "flex-start",

                    gap: 0.45,
                  }}
                >
                  {atendido ? (
                    <CheckCircleRoundedIcon
                      sx={{
                        color: "#087cf0",

                        fontSize: 14,

                        mt: 0.05,

                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <RadioButtonUncheckedRoundedIcon
                      sx={{
                        color: "#91a3b8",

                        fontSize: 14,

                        mt: 0.05,

                        flexShrink: 0,
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      color: atendido ? "#31516f" : "#64758a",

                      fontSize: tamanhosModaisGlobais.textoAuxiliar,

                      lineHeight: 1.15,
                    }}
                  >
                    {requisito.texto}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Typography
            sx={{
              mt: { xs: 0.55, md: 0.25 },

              mb: { xs: 0.3, md: 0.125 },

              color: "#405670",

              fontSize: tamanhosControlesGlobais.rotulo,

              fontWeight: 700,

              lineHeight: 1.2,
            }}
          >
            Força da senha
          </Typography>

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: `repeat(${requisitosSenha.length}, 1fr)`,

              gap: 0.35,
            }}
          >
            {requisitosSenha.map((requisito, index) => (
              <Box
                key={requisito.texto}
                sx={{
                  height: 4,

                  borderRadius: 99,

                  bgcolor: index < requisitosAtendidos ? "#087cf0" : "#c9d5e2",

                  transition: "background-color 120ms ease",
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* AÇÕES */}

      <Box
        sx={{
          display: "flex",

          justifyContent: "flex-end",

          flexWrap: "wrap",

          gap: 0.75,

          mt: 0.55,
        }}
      >
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={limparFormulario}
          disabled={salvando}
          sx={{
            minWidth: 100,

            minHeight: 30,

            textTransform: "none",

            fontSize: tamanhosModaisGlobais.botao,

            fontWeight: 600,
          }}
        >
          Cancelar
        </Button>

        <Button
          type="button"
          variant="contained"
          size="small"
          startIcon={
            <LockRoundedIcon
              sx={{
                fontSize: "17px !important",
              }}
            />
          }
          onClick={alterarSenha}
          disabled={
            salvando ||
            !senhaAtual ||
            !novaSenha ||
            !confirmarNovaSenha ||
            !senhaValida
          }
          sx={{
            minWidth: 145,

            minHeight: 30,

            textTransform: "none",

            fontSize: tamanhosModaisGlobais.botao,

            fontWeight: 600,

            bgcolor: "#087cf0",

            boxShadow: "none",

            "&:hover": {
              bgcolor: "#0767d8",

              boxShadow: "none",
            },
          }}
        >
          {salvando ? "Atualizando..." : "Atualizar senha"}
        </Button>
      </Box>
    </Box>
  );
}
